/**
 * Lógica PURA de la cola de subida (sin React ni red): estados, velocidad, ETA,
 * formato y el reducer que mantiene la lista de ítems. La usa el store
 * `Pages/aseos/components/colaSubidaStore.ts`; está aparte para poder probarla
 * sin DOM.
 */

export type EstadoItemCola =
  | "en_espera"
  | "comprimiendo"
  | "subiendo"
  | "reintentando"
  | "sin_conexion"
  | "procesando"
  | "listo"
  | "error"
  | "cancelado";

export type KindArchivo = "image" | "video" | "doc";

export interface ItemCola<TTipo extends string = string> {
  id: string;
  /** Aseo al que pertenece (la cola es global y sobrevive al modal). */
  aseoId: number;
  tipo: TTipo;
  /** El File se conserva en memoria para poder Reintentar sin volver a elegirlo. */
  file: File;
  nombre: string;
  mime: string;
  kind: KindArchivo;
  total: number;
  bytes: number;
  porcentaje: number;
  velocidadBps: number;
  etaSeg: number | null;
  estado: EstadoItemCola;
  /** Mensaje de error (estado "error") o aviso (reintentando / sin conexión). */
  mensaje: string | null;
  /**
   * Solo en estado "error": el servidor rechazó el archivo mismo (tipo no permitido,
   * imagen falsa, demasiado grande). Reintentar daría lo mismo: la UI ofrece solo «Quitar».
   */
  noReintentable?: boolean;
  intentos: number;
  previewUrl: string | null;
  creadoEn: number;
}

/** Estados en los que el ítem todavía consume o va a consumir red/CPU. */
const ACTIVOS: ReadonlySet<EstadoItemCola> = new Set([
  "en_espera",
  "comprimiendo",
  "subiendo",
  "reintentando",
  "sin_conexion",
  "procesando",
]);

export const estaActivo = (e: EstadoItemCola): boolean => ACTIVOS.has(e);

/** ¿Está transfiriendo ahora mismo (no solo esperando su turno)? */
export const estaEnCurso = (e: EstadoItemCola): boolean =>
  e === "comprimiendo" ||
  e === "subiendo" ||
  e === "reintentando" ||
  e === "sin_conexion" ||
  e === "procesando";

export const hayActivos = (items: readonly ItemCola[]): boolean =>
  items.some((i) => estaActivo(i.estado));

/** Tipos MIME por extensión, para archivos que el navegador entrega sin `type` (algunos Android). */
const MIME_POR_EXTENSION: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  heic: "image/heic",
  heif: "image/heif",
  mp4: "video/mp4",
  m4v: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  "3gp": "video/3gpp",
  mkv: "video/x-matroska",
  avi: "video/x-msvideo",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain",
  csv: "text/csv",
};

/** `file.type`, o el deducido de la extensión cuando el navegador lo dejó vacío. */
export function mimeDeArchivo(file: { type?: string; name?: string }): string {
  if (file.type) return file.type;
  const ext = (file.name ?? "").split(".").pop()?.toLowerCase() ?? "";
  return MIME_POR_EXTENSION[ext] ?? "";
}

export function kindDeArchivo(mime: string | undefined | null): KindArchivo {
  const m = (mime ?? "").toLowerCase();
  if (m.startsWith("image/")) return "image";
  if (m.startsWith("video/")) return "video";
  return "doc";
}

// ─── Velocidad y ETA ─────────────────────────────────────────────────────────

/**
 * Velocidad de subida con ventana deslizante: promedia los últimos `ventanaMs`
 * de muestras (bytes acumulados, instante). Suaviza los picos de red móvil sin
 * dejar de reaccionar si la señal se cae.
 */
export class MedidorVelocidad {
  private muestras: Array<{ t: number; bytes: number }> = [];

  constructor(private readonly ventanaMs = 8000) {}

  /** Registra los bytes ACUMULADOS enviados en el instante `t` (ms). */
  registrar(bytes: number, t: number): void {
    const ult = this.muestras[this.muestras.length - 1];
    // Si el contador retrocede (se reanudó desde otro offset), reiniciar la ventana.
    if (ult && bytes < ult.bytes) this.muestras = [];
    this.muestras.push({ t, bytes });
    const limite = t - this.ventanaMs;
    while (this.muestras.length > 2 && this.muestras[1].t < limite) {
      this.muestras.shift();
    }
  }

  /** Bytes por segundo (0 si todavía no hay datos suficientes). */
  velocidadBps(): number {
    if (this.muestras.length < 2) return 0;
    const a = this.muestras[0];
    const b = this.muestras[this.muestras.length - 1];
    const dt = (b.t - a.t) / 1000;
    if (dt <= 0) return 0;
    return Math.max(0, (b.bytes - a.bytes) / dt);
  }

  reiniciar(): void {
    this.muestras = [];
  }
}

/** Segundos que faltan, o null si no se puede estimar. */
export function calcularEta(restantes: number, bps: number): number | null {
  if (!(restantes > 0)) return 0;
  if (!(bps > 1)) return null;
  return Math.ceil(restantes / bps);
}

export function calcularPorcentaje(bytes: number, total: number): number {
  if (!(total > 0)) return 0;
  const p = (bytes / total) * 100;
  return Math.max(0, Math.min(100, Math.floor(p)));
}

// ─── Formato ─────────────────────────────────────────────────────────────────

export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  const dec = i === 0 || v >= 100 ? 0 : v >= 10 ? 1 : 2;
  return `${v.toFixed(dec).replace(".", ",")} ${u[i]}`;
}

export function formatVelocidad(bps: number): string {
  return bps > 0 ? `${formatBytes(bps)}/s` : "";
}

/** "2 min 05 s", "45 s", "1 h 03 min". Vacío si no hay estimación. */
export function formatEta(seg: number | null): string {
  if (seg == null || !Number.isFinite(seg)) return "";
  if (seg < 1) return "menos de 1 s";
  const s = Math.round(seg);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ${String(s % 60).padStart(2, "0")} s`;
  const h = Math.floor(m / 60);
  return `${h} h ${String(m % 60).padStart(2, "0")} min`;
}

/** Texto corto del estado, para la etiqueta del ítem. */
export function textoEstado(i: Pick<ItemCola, "estado" | "intentos">): string {
  switch (i.estado) {
    case "en_espera":
      return "En espera";
    case "comprimiendo":
      return "Optimizando…";
    case "subiendo":
      return "Subiendo";
    case "reintentando":
      return `Reintentando (${i.intentos})…`;
    case "sin_conexion":
      return "Sin conexión, en pausa";
    case "procesando":
      return "Procesando en el servidor…";
    case "listo":
      return "Listo";
    case "error":
      return "No se pudo subir";
    case "cancelado":
      return "Cancelado";
  }
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

export type AccionCola =
  | { tipo: "encolar"; item: ItemCola }
  | { tipo: "estado"; id: string; estado: EstadoItemCola; mensaje?: string | null }
  | {
      tipo: "progreso";
      id: string;
      bytes: number;
      total: number;
      porcentaje: number;
      velocidadBps: number;
      etaSeg: number | null;
      estado?: EstadoItemCola;
      mensaje?: string | null;
      intentos?: number;
    }
  | { tipo: "listo"; id: string }
  | { tipo: "error"; id: string; mensaje: string; noReintentable?: boolean }
  | { tipo: "cancelar"; id: string }
  | { tipo: "reintentar"; id: string }
  | { tipo: "quitar"; id: string };

const sinProgreso = {
  bytes: 0,
  porcentaje: 0,
  velocidadBps: 0,
  etaSeg: null,
} as const;

/** Transición de estados permitida: no se "resucita" un ítem terminado por un evento tardío. */
const TERMINALES: ReadonlySet<EstadoItemCola> = new Set(["listo", "cancelado"]);

export function colaReducer(state: ItemCola[], a: AccionCola): ItemCola[] {
  switch (a.tipo) {
    case "encolar":
      return [...state, a.item];

    case "quitar":
      return state.filter((i) => i.id !== a.id);

    case "estado":
      return state.map((i) =>
        i.id === a.id && !TERMINALES.has(i.estado)
          ? { ...i, estado: a.estado, mensaje: a.mensaje ?? null }
          : i,
      );

    case "progreso":
      return state.map((i) => {
        if (i.id !== a.id || TERMINALES.has(i.estado) || i.estado === "error") return i;
        return {
          ...i,
          bytes: a.bytes,
          total: a.total || i.total,
          porcentaje: a.porcentaje,
          velocidadBps: a.velocidadBps,
          etaSeg: a.etaSeg,
          estado: a.estado ?? i.estado,
          mensaje: a.mensaje !== undefined ? a.mensaje : i.mensaje,
          intentos: a.intentos ?? i.intentos,
        };
      });

    case "listo":
      return state.map((i) =>
        i.id === a.id && i.estado !== "cancelado"
          ? {
              ...i,
              estado: "listo",
              bytes: i.total,
              porcentaje: 100,
              velocidadBps: 0,
              etaSeg: 0,
              mensaje: null,
            }
          : i,
      );

    case "error":
      return state.map((i) =>
        i.id === a.id && !TERMINALES.has(i.estado)
          ? {
              ...i,
              estado: "error",
              mensaje: a.mensaje,
              noReintentable: a.noReintentable === true,
              velocidadBps: 0,
              etaSeg: null,
            }
          : i,
      );

    case "cancelar":
      return state.map((i) =>
        i.id === a.id && i.estado !== "listo"
          ? { ...i, estado: "cancelado", mensaje: null, velocidadBps: 0, etaSeg: null }
          : i,
      );

    case "reintentar":
      return state.map((i) =>
        i.id === a.id && (i.estado === "error" || i.estado === "cancelado")
          ? { ...i, ...sinProgreso, estado: "en_espera", mensaje: null, noReintentable: false, intentos: 0 }
          : i,
      );
  }
}

/** Siguiente ítem que debe procesarse (FIFO por orden de encolado). */
export function siguientePendiente(items: readonly ItemCola[]): ItemCola | null {
  return items.find((i) => i.estado === "en_espera") ?? null;
}
